<script lang="ts">
  import Icon from './Icon.svelte'
  import { t } from './i18n.svelte'
  import type { Problem } from './problems'

  let {
    problem,
    onRetry,
    onBack,
    backLabel,
    compact = false,
  }: {
    problem: Problem
    onRetry?: () => void
    onBack?: () => void
    backLabel?: string
    compact?: boolean
  } = $props()
</script>

<section
  class="problem"
  class:problem--quiet={problem.tone === 'quiet'}
  class:problem--warning={problem.tone === 'warning'}
  class:problem--compact={compact}
  role={problem.tone === 'error' ? 'alert' : 'status'}
  aria-live="polite"
  aria-atomic="true"
>
  <div class="problem__icon">
    <Icon
      name={problem.tone === 'quiet' ? 'eye' : problem.tone === 'warning' ? 'sync' : 'warn'}
      size={compact ? 20 : 28}
    />
  </div>
  <div class="problem__body">
    <h2>{problem.title}</h2>
    <p>{problem.message}</p>
    {#if (problem.retry && onRetry) || onBack}
      <div class="problem__actions">
        {#if problem.retry && onRetry}<button type="button" class="primary" onclick={onRetry}
            >{t('Try again')}</button
          >{/if}
        {#if onBack}<button type="button" class="ghost" onclick={onBack}
            >{backLabel ?? t('Back to home')}</button
          >{/if}
      </div>
    {/if}
    {#if problem.detail}
      <details class="problem__details">
        <summary>{t('Technical details')}</summary>
        <pre>{problem.detail}</pre>
      </details>
    {/if}
  </div>
</section>

<style>
  .problem {
    display: flex;
    gap: 16px;
    padding: 28px;
    border: 1px solid var(--border);
    border-radius: 18px;
    background: var(--bg-raised);
    color: var(--text);
    text-align: left;
    max-width: 560px;
    margin: auto;
  }
  .problem__icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    color: var(--accent);
    background: color-mix(in srgb, var(--accent) 12%, transparent);
  }
  .problem--quiet .problem__icon {
    color: var(--text-dim);
    background: color-mix(in srgb, var(--text-dim) 10%, transparent);
  }
  .problem__body {
    min-width: 0;
  }
  h2 {
    margin: 0 0 8px;
    font-size: 1.15rem;
    line-height: 1.35;
  }
  p {
    margin: 0;
    color: var(--text-dim);
    font-size: 0.94rem;
    line-height: 1.6;
  }
  .problem__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 20px;
  }
  .problem__details {
    margin-top: 16px;
    font-size: 0.8rem;
    color: var(--text-dim);
  }
  summary {
    cursor: pointer;
    padding: 4px 0;
  }
  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    max-height: 140px;
    overflow: auto;
    font-size: 0.75rem;
  }
  .problem--compact {
    max-width: none;
    margin: 0;
    padding: 16px;
    gap: 12px;
    border-radius: 12px;
  }
  .problem--compact .problem__icon {
    width: 32px;
    height: 32px;
  }
  .problem--compact h2 {
    font-size: 0.95rem;
    margin-bottom: 4px;
  }
  .problem--compact p {
    font-size: 0.88rem;
  }
  @media (max-width: 480px) {
    .problem {
      padding: 20px;
      gap: 12px;
    }
    .problem__icon {
      width: 36px;
      height: 36px;
    }
  }
</style>
